import { system } from '@/api/system.js'

var tasks = []
var listeners = []
var aborters = {}
var seq = 1

function emit () {
  var snapshot = tasks.slice()
  for (var i = 0; i < listeners.length; i++) {
    listeners[i](snapshot)
  }
}

function subscribe (fn) {
  listeners.push(fn)
  fn(tasks.slice())
  return function () {
    listeners = listeners.filter(function (item) { return item !== fn })
  }
}

function baseName (fileName) {
  var name = String(fileName || 'download')
  var slash = Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\'))
  if (slash >= 0) name = name.substring(slash + 1)
  name = name.replace(/[\\/:*?"<>|]/g, '_')
  return name || 'download'
}

function createTask (name) {
  var task = {
    id: seq++,
    name: name,
    percent: 0,
    status: 'downloading',
    message: '',
    received: 0,
    total: 0
  }
  tasks.push(task)
  emit()
  return task
}

function patchTask (id, patch) {
  for (var i = 0; i < tasks.length; i++) {
    if (tasks[i].id === id) {
      var next = {}
      var key
      for (key in tasks[i]) {
        if (Object.prototype.hasOwnProperty.call(tasks[i], key)) next[key] = tasks[i][key]
      }
      for (key in patch) {
        if (Object.prototype.hasOwnProperty.call(patch, key)) next[key] = patch[key]
      }
      tasks.splice(i, 1, next)
      emit()
      return next
    }
  }
  return null
}

function removeTask (id) {
  var abort = aborters[id]
  if (abort) {
    delete aborters[id]
    abort()
  }
  tasks = tasks.filter(function (item) { return item.id !== id })
  emit()
}

function readErrorMessage (res) {
  return res.text().then(function (text) {
    var msg = '下载失败'
    var trimmed = (text || '').replace(/^\s+|\s+$/g, '')
    if (trimmed && trimmed.charAt(0) === '{') {
      try {
        var json = JSON.parse(trimmed)
        msg = json.msg || json.message || msg
      } catch (e) { /* 非 JSON 时用默认文案 */ }
    }
    return msg
  }).catch(function () {
    return '下载失败'
  })
}

function discardHandle (handle) {
  if (!handle || typeof handle.remove !== 'function') return Promise.resolve()
  return handle.remove().catch(function () {})
}

function streamToFile (handle, urlPath, token, task) {
  var controller = typeof AbortController === 'function' ? new AbortController() : null
  var cancelled = false
  aborters[task.id] = function () {
    cancelled = true
    if (controller) controller.abort()
  }
  var url = system.requestBaseUrl + system.cssServerName + urlPath
  var init = {
    headers: { Authorization: 'Bearer ' + token }
  }
  if (controller) init.signal = controller.signal
  return fetch(url, init).then(function (res) {
    var contentType = (res.headers.get('content-type') || '').toLowerCase()
    if (!res.ok || contentType.indexOf('json') >= 0) {
      return readErrorMessage(res).then(function (msg) {
        throw new Error(msg)
      })
    }
    if (!res.body || typeof res.body.getReader !== 'function') {
      throw new Error('当前浏览器不支持流式下载')
    }
    var total = Number(res.headers.get('content-length')) || 0
    return handle.createWritable().then(function (writable) {
      var reader = res.body.getReader()
      var received = 0
      var lastEmit = 0
      var lastPercent = -1
      var settled = false
      function publish (force) {
        var percent = total > 0 ? Math.floor(received * 100 / total) : 0
        if (!force && percent >= 100) percent = 99
        var now = Date.now()
        if (!force && now - lastEmit < 300 && percent === lastPercent) return
        lastEmit = now
        lastPercent = percent
        patchTask(task.id, {
          status: 'downloading',
          percent: percent,
          received: received,
          total: total
        })
      }
      function failWrite (err) {
        var abortWrite = writable.abort ? writable.abort() : Promise.resolve()
        return Promise.resolve(abortWrite).catch(function () {}).then(function () {
          return discardHandle(handle)
        }).then(function () {
          if (cancelled || (err && err.name === 'AbortError')) {
            patchTask(task.id, { status: 'cancelled', message: '已取消' })
            return
          }
          patchTask(task.id, { status: 'error', message: (err && err.message) || '下载失败' })
        })
      }
      function pump () {
        return reader.read().then(function (result) {
          if (result.done) {
            return writable.close().then(function () {
              settled = true
              patchTask(task.id, { status: 'done', percent: 100, received: received, total: total || received })
            })
          }
          received += result.value.byteLength || result.value.length || 0
          publish(false)
          return writable.write(result.value).then(pump)
        })
      }
      publish(true)
      return pump().catch(function (err) {
        if (settled) throw err
        return failWrite(err)
      })
    })
  }).catch(function (err) {
    return discardHandle(handle).then(function () {
      if (cancelled || (err && err.name === 'AbortError')) {
        patchTask(task.id, { status: 'cancelled', message: '已取消' })
        return
      }
      patchTask(task.id, { status: 'error', message: (err && err.message) || '下载失败' })
    })
  }).then(function () {
    delete aborters[task.id]
  })
}

var MAX_BLOB_BYTES = 1536 * 1024 * 1024

function downloadUrl (urlPath) {
  return system.requestBaseUrl + system.cssServerName + urlPath
}

function handToBrowser (urlPath, token, task) {
  var url = downloadUrl(urlPath)
  var join = url.indexOf('?') >= 0 ? '&' : '?'
  var iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.src = url + join + 'access_token=' + encodeURIComponent(token) + '&_=' + Date.now()
  document.body.appendChild(iframe)
  patchTask(task.id, {
    status: 'browser',
    percent: 0,
    total: 0,
    received: 0,
    message: '文件较大，进度在浏览器下载列表里'
  })
}

function saveBlobFile (blob, fileName) {
  var url = URL.createObjectURL(blob)
  var link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(function () { URL.revokeObjectURL(url) }, 2000)
}

function streamWithoutPicker (urlPath, token, task, fileName) {
  var controller = typeof AbortController === 'function' ? new AbortController() : null
  var cancelled = false
  aborters[task.id] = function () {
    cancelled = true
    if (controller) controller.abort()
  }
  var init = { headers: { Authorization: 'Bearer ' + token } }
  if (controller) init.signal = controller.signal
  return fetch(downloadUrl(urlPath), init).then(function (res) {
    var contentType = (res.headers.get('content-type') || '').toLowerCase()
    if (!res.ok || contentType.indexOf('json') >= 0) {
      return readErrorMessage(res).then(function (msg) {
        throw new Error(msg)
      })
    }
    if (!res.body || typeof res.body.getReader !== 'function') {
      throw new Error('当前浏览器不支持流式下载')
    }
    var total = Number(res.headers.get('content-length')) || 0
    if (total > MAX_BLOB_BYTES) {
      if (typeof res.body.cancel === 'function') res.body.cancel()
      handToBrowser(urlPath, token, task)
      return
    }
    var reader = res.body.getReader()
    var chunks = []
    var received = 0
    var lastEmit = 0
    var lastPercent = -1
    function publish (force) {
      var percent = total > 0 ? Math.floor(received * 100 / total) : 0
      if (!force && total > 0 && percent >= 100) percent = 99
      var now = Date.now()
      if (!force && now - lastEmit < 300 && percent === lastPercent) return
      lastEmit = now
      lastPercent = percent
      patchTask(task.id, {
        status: 'downloading',
        percent: percent,
        received: received,
        total: total
      })
    }
    function pump () {
      return reader.read().then(function (result) {
        if (result.done) {
          saveBlobFile(new Blob(chunks), fileName)
          chunks = []
          patchTask(task.id, { status: 'done', percent: 100, received: received, total: total || received })
          return
        }
        var size = result.value.byteLength || result.value.length || 0
        if (received + size > MAX_BLOB_BYTES) {
          chunks = []
          reader.cancel()
          handToBrowser(urlPath, token, task)
          return
        }
        chunks.push(result.value)
        received += size
        publish(false)
        return pump()
      })
    }
    publish(true)
    return pump()
  }).catch(function (err) {
    if (cancelled || (err && err.name === 'AbortError')) {
      patchTask(task.id, { status: 'cancelled', message: '已取消' })
      return
    }
    patchTask(task.id, { status: 'error', message: (err && err.message) || '下载失败' })
  }).then(function () {
    delete aborters[task.id]
  })
}

function startStreamDownload (urlPath, fileName) {
  var token = sessionStorage.getItem('access_token')
  if (!token) {
    return { ok: false, message: '登录已失效，请重新登录' }
  }
  var suggested = baseName(fileName)
  if (typeof window.showSaveFilePicker !== 'function') {
    var task = createTask(suggested)
    streamWithoutPicker(urlPath, token, task, suggested)
    return { ok: true }
  }
  var picker
  try {
    picker = window.showSaveFilePicker({
      suggestedName: suggested,
      id: 'css-attachment-download'
    })
  } catch (err) {
    return { ok: false, message: '无法打开保存位置' }
  }
  picker.then(function (handle) {
    var picked = createTask(suggested)
    return streamToFile(handle, urlPath, token, picked)
  }, function (err) {
    if (err && err.name === 'AbortError') return
    var failed = createTask(suggested)
    patchTask(failed.id, { status: 'error', message: (err && err.message) || '无法选择保存位置' })
  })
  return { ok: true }
}

function startAttachmentDownload (fileName, type) {
  var urlPath = '/file/stream/' + encodeURIComponent(type)
    + '?fileName=' + encodeURIComponent(fileName)
  return startStreamDownload(urlPath, fileName)
}

export {
  subscribe,
  removeTask,
  startAttachmentDownload,
  startStreamDownload,
  baseName
}



// WEBPACK FOOTER //
// ./src/components/upload/downloadManager.js